import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCustodianPendingAdmission, listCustodianVaultAssets } from '../../api/custodianApi';
import { DataTable } from '../ui/DataTable';
import { ErrorBanner } from '../ui/ErrorBanner';
import { StatusPill } from '../ui/StatusPill';
import { holderLabel } from '../custody/AuthorityStateCard';
import {
  custodianPackageDetailPath,
  displayValue,
} from '../../pages/custodian/custodianReviewLabels';

function formatWhen(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return displayValue(value);
  return date.toLocaleString();
}

export function AdmittedSessionPanel() {
  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      const [vaultResult, pendingResult] = await Promise.allSettled([
        listCustodianVaultAssets({ page: 1, pageSize: 50 }),
        getCustodianPendingAdmission({ page: 1, pageSize: 50 }),
      ]);
      if (cancelled) return;
      if (vaultResult.status === 'fulfilled') {
        setRows(vaultResult.value?.items || []);
      } else {
        setRows([]);
        setError(vaultResult.reason?.message || 'Unable to load admitted vault assets.');
      }
      // The awaiting list is secondary: if it fails we still show the register.
      setPending(pendingResult.status === 'fulfilled' ? pendingResult.value?.items || [] : []);
      setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const pendingColumns = [
    {
      key: 'package_id',
      header: 'Package',
      render: (row) => (
        <Link
          to={custodianPackageDetailPath(row.package_id)}
          className="font-mono text-[11px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
        >
          {displayValue(row.reference_number || row.package_id)}
        </Link>
      ),
    },
    { key: 'originator', header: 'Originator', render: (row) => displayValue(row.originator) },
    { key: 'program', header: 'Program', render: (row) => displayValue(row.program) },
    {
      key: 'authority_state',
      header: 'Authority',
      render: (row) =>
        row.authority_state ? <StatusPill value={row.authority_state} /> : displayValue(row.authority_state),
    },
    {
      key: 'file_count',
      header: 'Files',
      align: 'right',
      render: (row) => <span className="tabular-nums">{row.file_count ?? 0}</span>,
    },
    { key: 'routed_at', header: 'Routed', render: (row) => formatWhen(row.routed_at) },
    {
      key: 'action',
      header: 'Next step',
      render: (row) => (
        <Link
          to={custodianPackageDetailPath(row.package_id)}
          className="text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
        >
          Admit to Authoritative Vault →
        </Link>
      ),
    },
  ];

  const columns = [
    {
      key: 'package_id',
      header: 'Package',
      render: (row) => (
        <Link
          to={custodianPackageDetailPath(row.package_id)}
          className="font-mono text-[11px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
        >
          {displayValue(row.reference_number || row.package_id)}
        </Link>
      ),
    },
    {
      key: 'borrower',
      header: 'Borrower',
      render: (row) => displayValue(row.borrower),
    },
    {
      key: 'program',
      header: 'Program',
      render: (row) => displayValue(row.program),
    },
    {
      key: 'admission',
      header: 'Admission',
      render: (row) =>
        row.admission_state ? (
          <StatusPill value={row.admission_state} />
        ) : (
          displayValue(row.admission_state)
        ),
    },
    {
      key: 'custody',
      header: 'Custody',
      render: (row) =>
        row.custody_state ? (
          <StatusPill value={row.custody_state} />
        ) : (
          displayValue(row.custody_state)
        ),
    },
    {
      key: 'holder',
      header: 'Custodian',
      render: (row) =>
        row.current_holder ? (
          <StatusPill value={row.current_holder} label={holderLabel(row.current_holder)} />
        ) : (
          displayValue(row.current_holder)
        ),
    },
  ];

  return (
    <section className="space-y-3">
      <div className="rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card sm:p-5">
        <h2 className="text-sm font-semibold text-eorig-ink">Asset register</h2>
        <p className="mt-1 text-xs leading-relaxed text-eorig-muted">
          Batches approved by this custodian appear under <strong>Awaiting admission</strong> until
          Admit is run; after Admit they move to <strong>Admitted vault assets</strong> and stay
          there across sessions.
        </p>
      </div>
      {error ? <ErrorBanner message={error} onRetry={() => setReloadToken((value) => value + 1)} /> : null}

      {!loading && pending.length ? (
        <div className="space-y-2" aria-label="Awaiting admission">
          <h3 className="text-[13px] font-semibold text-eorig-ink">
            Awaiting admission{' '}
            <span className="font-normal text-eorig-muted">({pending.length})</span>
          </h3>
          <DataTable
            columns={pendingColumns}
            rows={pending}
            rowKey={(row) => row.asset_id || row.package_id}
            emptyMessage="Nothing is waiting for admission."
            minWidth={720}
          />
        </div>
      ) : null}

      <div className="space-y-2" aria-label="Admitted vault assets">
        <h3 className="text-[13px] font-semibold text-eorig-ink">
          Admitted vault assets{' '}
          {!loading ? <span className="font-normal text-eorig-muted">({rows.length})</span> : null}
        </h3>
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.vault_asset_id || row.package_id}
          loading={loading}
          emptyMessage={
            pending.length
              ? 'No packages admitted yet. Open a batch above and run Admit to Authoritative Vault.'
              : 'No packages have been admitted to the vault.'
          }
          minWidth={720}
        />
      </div>
    </section>
  );
}
